import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Edit, Trash2, Eye, Phone, Mail, Droplet, AlertCircle, Users, UserCheck, UserX, Plus, Filter, ChevronDown, Calendar, MapPin } from 'lucide-react';

const DonorList = () => {
  const navigate = useNavigate();
  const [donors, setDonors] = useState([]);
  const [filteredDonors, setFilteredDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    totalDonors: 0,
    availableDonors: 0
  });
  const [bloodGroupDistribution, setBloodGroupDistribution] = useState({
    'A+': 0, 'A-': 0, 'B+': 0, 'B-': 0, 'AB+': 0, 'AB-': 0, 'O+': 0, 'O-': 0
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [donorToDelete, setDonorToDelete] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    bloodGroup: '',
    status: '',
    gender: ''
  });

  useEffect(() => {
    fetchDonors();
  }, []);

  const fetchDonors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/admin/donors`, {
        headers: {
          'x-auth-token': token
        }
      });
      
      setDonors(response.data.donors);
      setFilteredDonors(response.data.donors);
      setStats({
        totalDonors: response.data.totalDonors,
        availableDonors: response.data.availableDonors
      });
      
      // Set blood group distribution if available from API
      if (response.data.bloodGroupDistribution) {
        setBloodGroupDistribution(response.data.bloodGroupDistribution);
      } else {
        // Calculate blood group distribution from donors data
        const distribution = {
          'A+': 0, 'A-': 0, 'B+': 0, 'B-': 0, 'AB+': 0, 'AB-': 0, 'O+': 0, 'O-': 0
        };
        
        response.data.donors.forEach(donor => {
          if (donor.bloodGroup && distribution.hasOwnProperty(donor.bloodGroup)) {
            distribution[donor.bloodGroup]++;
          }
        });
        
        setBloodGroupDistribution(distribution);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching donors:', err);
      setError('Failed to fetch donors. Please try again.');
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim() === '') {
      setFilteredDonors(donors);
      return;
    }
    
    try {
      // If searching by email or phone, use the search API
      if (query.includes('@') || /^\d+$/.test(query)) {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/admin/donors/search?query=${query}`,
          {
            headers: {
              'x-auth-token': token
            }
          }
        );
        setFilteredDonors(response.data);
      } else {
        // Otherwise, filter locally
        const filtered = donors.filter(donor => 
          donor.name.toLowerCase().includes(query.toLowerCase()) ||
          (donor.bloodGroup && donor.bloodGroup.toLowerCase().includes(query.toLowerCase()))
        );
        setFilteredDonors(filtered);
      }
    } catch (err) {
      console.error('Error searching donors:', err);
      setError('Failed to search donors. Please try again.');
    }
  };

  const applyFilters = () => {
    let filtered = [...donors];
    
    if (filters.bloodGroup) {
      filtered = filtered.filter(donor => donor.bloodGroup === filters.bloodGroup);
    }
    
    if (filters.status) {
      filtered = filtered.filter(donor => {
        if (filters.status === 'available') return donor.isAvailable;
        if (filters.status === 'unavailable') return !donor.isAvailable;
        return true;
      });
    }
    
    if (filters.gender) {
      filtered = filtered.filter(donor => donor.gender && donor.gender.toLowerCase() === filters.gender.toLowerCase());
    }
    
    setFilteredDonors(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      bloodGroup: '',
      status: '',
      gender: ''
    });
    setFilteredDonors(donors);
  };

  const handleViewDonor = (id) => {
    navigate(`/admin/dashboard/donors/details/${id}`);
  };

  const handleEditDonor = (id) => {
    navigate(`/admin/dashboard/donors/form?id=${id}`);
  };

  const confirmDelete = (donor) => {
    setDonorToDelete(donor);
    setShowDeleteModal(true);
  };

  const handleDeleteDonor = async () => {
    if (!donorToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/admin/donors/${donorToDelete._id}`,
        {
          headers: {
            'x-auth-token': token
          }
        }
      );
      
      // Remove the deleted donor from the list
      setDonors(donors.filter(d => d._id !== donorToDelete._id));
      setFilteredDonors(filteredDonors.filter(d => d._id !== donorToDelete._id));
      setShowDeleteModal(false);
      setDonorToDelete(null);
      
      // Update stats
      setStats(prev => ({
        ...prev,
        totalDonors: prev.totalDonors - 1
      }));
      
      // Update blood group distribution
      if (donorToDelete.bloodGroup && bloodGroupDistribution.hasOwnProperty(donorToDelete.bloodGroup)) {
        setBloodGroupDistribution(prev => ({
          ...prev,
          [donorToDelete.bloodGroup]: Math.max(0, prev[donorToDelete.bloodGroup] - 1)
        }));
      }
      
      // Show success message
      alert('Donor deleted successfully');
    } catch (err) {
      console.error('Error deleting donor:', err);
      alert('Failed to delete donor. Please try again.');
    }
  };

  const handleAddDonor = () => {
    navigate('/admin/dashboard/donors/form');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm text-blue-100">Total Donors</h3>
              <p className="text-3xl font-bold mt-1">{stats.totalDonors}</p>
              <div className="flex items-center mt-3 text-xs text-blue-100">
                <Users className="w-3 h-3 mr-1" />
                <span>All registered donors</span>
              </div>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <Users className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm text-green-100">Available Donors</h3>
              <p className="text-3xl font-bold mt-1">{stats.availableDonors}</p>
              <div className="flex items-center mt-3 text-xs text-green-100">
                <UserCheck className="w-3 h-3 mr-1" />
                <span>Ready to donate</span>
              </div>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <UserCheck className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm text-yellow-100">Unavailable Donors</h3>
              <p className="text-3xl font-bold mt-1">{stats.totalDonors - stats.availableDonors}</p>
              <div className="flex items-center mt-3 text-xs text-yellow-100">
                <UserX className="w-3 h-3 mr-1" />
                <span>Not eligible currently</span>
              </div>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <UserX className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Blood Group Distribution */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Blood Group Distribution</h3>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-4">
          {Object.entries(bloodGroupDistribution).map(([type, count]) => (
            <div key={type} className="flex flex-col items-center">
              <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${type.includes('A') ? 'from-red-500 to-red-600' : 
                type.includes('B') && !type.includes('AB') ? 'from-blue-500 to-blue-600' : 
                type.includes('AB') ? 'from-purple-500 to-purple-600' : 
                'from-green-500 to-green-600'} flex items-center justify-center text-white font-bold shadow-md`}>
                {type}
              </div>
              <span className="mt-2 font-semibold text-gray-700">{count}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0 mb-6">
          <div className="w-full lg:w-1/2 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
              placeholder="Search donors by name, email, phone or blood group"
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>
          
          <div className="flex space-x-3 w-full lg:w-auto">
            <button 
              onClick={() => setFilterOpen(!filterOpen)}
              className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${filterOpen ? 'transform rotate-180' : ''}`} />
            </button>
            
            <button 
              onClick={handleAddDonor}
              className="flex items-center justify-center bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 rounded-lg hover:from-red-700 hover:to-red-800 transition-colors shadow-md"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Donor
            </button>
          </div>
        </div>
        
        {/* Filter options */}
        {filterOpen && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6 animate-fadeIn">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                <select
                  name="bloodGroup"
                  value={filters.bloodGroup}
                  onChange={handleFilterChange}
                  className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                >
                  <option value="">All Blood Groups</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                >
                  <option value="">All Status</option>
                  <option value="available">Available</option>
                  <option value="unavailable">Unavailable</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  name="gender"
                  value={filters.gender}
                  onChange={handleFilterChange}
                  className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                >
                  <option value="">All Genders</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end mt-4 space-x-3">
              <button
                onClick={resetFilters}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 text-sm"
              >
                Reset
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 text-sm"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
        
        {/* Donors List */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Blood Group</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredDonors.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <Users className="h-8 w-8 text-gray-300 mb-2" />
                      <p className="text-gray-500 font-medium">No donors found</p>
                      <p className="text-gray-400 text-sm">Try adjusting your search or filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredDonors.map((donor) => (
                  <tr key={donor._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white font-medium shadow-sm">
                          {donor.name.charAt(0)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{donor.name}</div>
                          <div className="text-xs text-gray-500 flex items-center mt-1">
                            <Calendar className="h-3 w-3 mr-1" />
                            {donor.age} years, {donor.gender}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <Mail className="h-4 w-4 mr-1 text-gray-400" />
                        {donor.email}
                      </div>
                      {donor.phone && (
                        <div className="text-sm text-gray-500 flex items-center mt-1">
                          <Phone className="h-4 w-4 mr-1 text-gray-400" />
                          {donor.phone}
                        </div>
                      )}
                      {donor.city && donor.state && (
                        <div className="text-xs text-gray-500 flex items-center mt-1">
                          <MapPin className="h-3 w-3 mr-1 text-gray-400" />
                          {donor.city}, {donor.state}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-xs shadow-sm ${
                          donor.bloodGroup?.includes('A') && !donor.bloodGroup?.includes('AB') ? 'bg-gradient-to-br from-red-500 to-red-600' : 
                          donor.bloodGroup?.includes('B') && !donor.bloodGroup?.includes('AB') ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 
                          donor.bloodGroup?.includes('AB') ? 'bg-gradient-to-br from-purple-500 to-purple-600' : 
                          'bg-gradient-to-br from-green-500 to-green-600'
                        }`}>
                          {donor.bloodGroup || '?'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${donor.isAvailable ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {donor.isAvailable ? 'Available' : 'Unavailable'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-1">
                        <button 
                          onClick={() => handleViewDonor(donor._id)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100 transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => handleEditDonor(donor._id)}
                          className="text-yellow-600 hover:text-yellow-900 p-1 rounded-full hover:bg-yellow-100 transition-colors"
                          title="Edit Donor"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => confirmDelete(donor)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
                          title="Delete Donor"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative mx-auto p-6 border w-96 shadow-lg rounded-xl bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-3">Delete Donor</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete {donorToDelete?.name}? This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-center mt-5 space-x-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteDonor}
                  className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-md hover:from-red-700 hover:to-red-800 focus:outline-none transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DonorList;